/**
 * スペース利用可能時間表示のReactコンポーネントをマウントするモジュール
 */
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import SpaceAvailabilityCalendar from './SpaceAvailabilityCalendar';

/**
 * SpaceAvailabilityCalendarコンポーネントをマウントする
 * @param element マウント対象のDOM要素
 */
const mountComponent = (element: Element): void => {
  // data-space-id属性からスペースIDを取得
  const spaceId = element.getAttribute('data-space-id');

  if (!spaceId) {
    console.error('Space ID not found for element', element);
    return;
  }

  // isList判定をdata-space-list-type="list"属性の有無で判定
  const isList = element.getAttribute('data-space-list-type') === 'list';

  try {
    // このエレメントにReactをマウント
    const root = createRoot(element);
    root.render(
      <React.StrictMode>
        <SpaceAvailabilityCalendar
          spaceId={Number.parseInt(spaceId, 10)}
          isList={isList}
        />
      </React.StrictMode>
    );
  } catch (error) {
    console.error('Failed to mount SpaceAvailabilityCalendar:', error);
  }
};

/**
 * SpaceAvailabilityCalendarコンポーネントのマウント処理を初期化
 * IntersectionObserverを使用して表示範囲内に入った要素だけマウントする
 */
export const initialize = (): (() => void) => {
  // 交差を監視対象のすべての.js-spaceDetail要素を取得
  const detailElements = document.querySelectorAll<HTMLElement>('.js-spaceDetail');

  // NodeListをArrayに変換（TypeScriptエラー回避のため）
  const detailElementsArray = Array.from(detailElements);

  // すでにマウントした要素を記録するSet
  const mountedElements = new Set<Element>();

  // IntersectionObserverの設定
  const observerOptions = {
    // rootMargin: '200px', // 表示領域の200px手前に入ったときに判定
    threshold: 0.1        // 10%以上表示されたときに判定
  };

  // IntersectionObserverを作成
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const element = entry.target;

      // 要素が表示範囲内に入り、まだマウントされていない場合
      if (entry.isIntersecting && !mountedElements.has(element)) {
        // コンポーネントをマウント
        mountComponent(element);

        // マウント済みとして記録
        mountedElements.add(element);

        // 一度マウントしたら監視を解除（再度マウントしないように）
        observer.unobserve(element);
      }
    }
  }, observerOptions);

  // 各要素の監視を開始
  for (const element of detailElementsArray) {
    observer.observe(element);
  }

  // ページ遷移時に備えてcleanup関数を返す (turbolinks:before-visitなどで呼び出すことを想定)
  return (): void => {
    // すべての要素の監視を解除
    for (const element of detailElementsArray) {
      observer.unobserve(element);
    }
    observer.disconnect();
  };
};